import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useIdStore, useViewedTypeStore } from "../../stores/selectedToViewStore";
import { ConsultingNavigationService } from "../../services/consultingNavigationService";
import Grid from "@mui/material/Grid2"; // שימוש ב-Grid v2
import { useActiveUserOrFamilyData } from "../../hooks/consulting/useActiveUserOrFamilyData";
import { useContactFamilyInfo } from "../../hooks/consulting/useContactFamilyInfo";
import { useFundData } from "../../hooks/consulting/useFundData";
import BeneficiaryChips from "../Pension/BeneficiaryChips";
import { useMimshaksByContactsAndPeriod, useMimshaksByContactAndPeriod } from "../../hooks/useMimshaksData";
import { ConsultingHeader } from "./shared/ConsultingHeader";
import { Avatar, Box, Chip, CircularProgress, Fab, Typography } from "@mui/material";
import SummarizeIcon from "@mui/icons-material/Summarize";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import StyledDrawer from "../../shared/UIParts/StyledDrawer";
import { useLatestPreAdviceMimshaksByContacts, useLatestPreAdviceMimshakByContact } from "../../hooks/useMimshaksDataPreAdvice";
import { useLatestManualMimshakByContact, useLatestManualMimshaksByContacts } from "../../hooks/useManualMimshaksData";
import { AdjustedProductType } from "../../interfaces/DTO/mislaka_xml_models";
import ExposurePage from "./ExposurePage";
import { PeriodDropdownSelector } from "./shared/PeriodDropdownSelector";
import { ReportedPeriod } from "../../interfaces/DTO/agent/mimshakGroupBy";
import { useViewedEntityName } from "../../hooks/useViewedEntityName";
import FeesLabPageDnD from "../FeesLab/FeesLabPageDnD";
import MainPage from "./MainPage";
import SelfBankPage from "../SelfBank/SelfBankPage";
import { ResponsiveConsultingTabs } from "./shared/ResponsiveConsultingTabs";
import { Favorite } from "@mui/icons-material";
import { PreAdviceRequestComponent } from "./PreAdviceRequestComponent";
import { DataAccessTypeEnum } from "../../interfaces/DTO/features/ShareContact";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { ContactsInsurancePage } from "../Insurance/ContactsInsurancePage";


// ✅ טעינה עצלה של קומפוננטות כבדות
const DepositsPage = React.lazy(() => import("../Deposits/DepositsPage"));
const PensionPage = React.lazy(() => import("../Pension/PensionPage"));
const GrowthSimulation = React.lazy(() => import("../PersonFinancialData/GrowthSimulation"));
const GrowthPage = React.lazy(() => import("../Grow/GrowthPage"));
const StoryPage = React.lazy(() => import("../Story/StoryPage").then(module => ({ default: module.StoryPage })));
const MeetingSummaryDrawer = React.lazy(() => import("./MeetingSummaryDrawer"));
const ConsultingContactInfo = React.lazy(() => import("./ConsultingContactInfo"));
const ContactCommentsPanel = React.lazy(() => import("../CRM/contacts/ContactCommentsPanel"));


export const UserConsultingPage: React.FC = () => {
  // === ✅ סטייטים גלובליים ===
  const id = useIdStore((state) => state.id);

  const viewedType = useViewedTypeStore((state) => state.viewedType);
  const setViewedType = useViewedTypeStore((state) => state.setViewedType);
  const setId = useIdStore((state) => state.setId);
  const name = useViewedEntityName();
  const [reportedPeriods, setReportedPeriods] = useState<ReportedPeriod[]>([]);
  const reportedPeriodsRef = useRef<Record<number, ReportedPeriod[]>>({});
  const [selectedPeriod, setSelectedPeriod] = useState<string | undefined>();
  const [selectedPeriodType, setSelectedPeriodType] = useState<"REGULAR" | "PRE_ADVICE">("REGULAR");
  const [meetingSummaryOpen, setMeetingSummaryOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsOpened, setCommentsOpened] = useState(false);
  const [contactInfoOpen, setContactInfoOpen] = useState(false);
  const [contactInfoOpened, setContactInfoOpened] = useState(false);
  const [contactInfoSection, setContactInfoSection] = useState<"contact" | "employment">("employment");
  const [contactInfoContacts, setContactInfoContacts] = useState<Contact[]>([]);
  const {
    contacts: contacts,
    familyMembers,
    isLoading: userDataLoading,
  } = useActiveUserOrFamilyData();

  // ✅ משפחת הלקוח הנוכחי (רק כשמציגים לקוח בודד) — נטען פעם אחת כאן ומועבר למטה כ-props
  const { familyInfo } = useContactFamilyInfo(
    viewedType === "USER" ? contacts[0]?.id : undefined
  );

  const handleFamilyClick = (familyId: number) => {
    setViewedType("FAMILY");
    setId(familyId);
    if (!ConsultingNavigationService.isPresentationMode()) {
      ConsultingNavigationService.pushToHistory({
        id: familyId,
        type: "FAMILY",
        preserveOtherParams: true,
      });
    }
  };
  // ✅ שליפת תקופות לפי משתמש

  
  // ✅ קריאה תמידית – במקום להציב בתנאי
  const mimshakResultByUser = useMimshaksByContactAndPeriod(id, selectedPeriod, viewedType === "USER");
  const mimshakResultByFamily = useMimshaksByContactsAndPeriod(
    contacts.map((u) => u.id!),
    selectedPeriod,
    viewedType === "FAMILY"
  );
// ✅ שליפת Mimshak לפי PRE_ADVICE – נקרא תמיד, אבל נשתמש רק אם selectedPeriodType === 'PRE_ADVICE'
const preAdviceDataResultByUser = useLatestPreAdviceMimshakByContact(
  id,
  selectedPeriodType === "PRE_ADVICE" && viewedType === "USER"
);

const preAdviceDataResultByFamily = useLatestPreAdviceMimshaksByContacts(
  contacts.map((u) => u.id!),
  selectedPeriodType === "PRE_ADVICE" && viewedType === "FAMILY"
);

const manualMimshakByUser = useLatestManualMimshakByContact(id, selectedPeriodType === "PRE_ADVICE" && viewedType === "USER");
const manualMimshakByFamily = useLatestManualMimshaksByContacts(
  contacts.map((u) => u.id!),
  selectedPeriodType === "PRE_ADVICE" && viewedType === "FAMILY"
);

useEffect(() => {
  if (!id) return;

  const userPeriods = reportedPeriodsRef.current[id];
  if (!userPeriods || userPeriods.length === 0) return; // ✋ חכה שהתקופות יגיעו

  const sorted = [...userPeriods].sort((a, b) =>
    b.reported_period.localeCompare(a.reported_period)
  );
  const first = sorted[0];

  setSelectedPeriod(first.reported_period);
  setSelectedPeriodType(first.type);
}, [id, reportedPeriods]); // ✅ חשוב לכלול גם reportedPeriods
useEffect(() => {
  const userPeriods = reportedPeriodsRef.current[id ?? -1] || [];
  const selectedExists = userPeriods.some(p => p.reported_period === selectedPeriod);
  if ((!selectedPeriod || !selectedExists) && userPeriods.length > 0) {
    const sorted = [...userPeriods].sort((a, b) =>
      b.reported_period.localeCompare(a.reported_period)
    );
    const first = sorted[0];
    setSelectedPeriod(first.reported_period);
    setSelectedPeriodType(first.type);
  }
}, [id, selectedPeriod, reportedPeriods]);

  // ✅ בחירת התוצאה הרלוונטית
  const mimshakResult = useMemo(() => {
    if (selectedPeriodType === "PRE_ADVICE") {
      if (viewedType === "USER") {
        return preAdviceDataResultByUser;
      } else if (viewedType === "FAMILY") {
        return preAdviceDataResultByFamily;
      }
    } else {
      if (viewedType === "USER") {
        return mimshakResultByUser;
      } else {
        return mimshakResultByFamily;
      }
    }
  }, [
    preAdviceDataResultByUser,
    preAdviceDataResultByFamily,
    mimshakResultByUser,
    mimshakResultByFamily,
    selectedPeriodType,
    viewedType,
  ]);
    
  const baseMimshakData = mimshakResult?.data || [];

  const mimshakData = useMemo(() => {
    if (selectedPeriodType !== "PRE_ADVICE") return baseMimshakData;
    const manualRaw = viewedType === "USER" ? manualMimshakByUser?.data : manualMimshakByFamily?.data;
    const manualSavings = (manualRaw || []).filter(
      (m) => (m.adjusted_product_type ?? m.product_type) === AdjustedProductType.PURE_SAVINGS_POLICY
    );
    if (manualSavings.length === 0) return baseMimshakData;
    const existingPolicyNumbers = new Set(baseMimshakData.map((m) => m.policy_number));
    const newOnes = manualSavings.filter((m) => !existingPolicyNumbers.has(m.policy_number));
    return newOnes.length > 0 ? [...baseMimshakData, ...newOnes] : baseMimshakData;
  }, [baseMimshakData, selectedPeriodType, viewedType, manualMimshakByUser?.data, manualMimshakByFamily?.data]);


  // === ✅ שליפת נתוני הקרנות מה-HOOK (כולל חלוקת Mimshak לפי סוג) ===
  const {
    flatMimshaks,
    allPolicies,
    allProducts,
    pensionProducts,
    liquidProducts,
    insuranceProducts,
    fundData,
    insuranceMimshakData,
    pensionMimshakData,
    capitalMimshakData,
  } = useFundData(mimshakData);


  // ✅ האם יש עדיין משהו בטעינה?
  const isFullLoading =
    userDataLoading ||
   
    (Array.isArray(mimshakResult)
      ? mimshakResult.some((result) => result.isLoading)
      : mimshakResult.isLoading);

  const handleFamilyMemberClick = (userId: number) => {
    // עדכון ה-store
    setViewedType("USER");
    setId(userId);

    // עדכון URL רק אם לא במצב תצוגה (במצב תצוגה ה-URL קפוא)
   
    if (!ConsultingNavigationService.isPresentationMode()) {
      ConsultingNavigationService.pushToHistory({
        id: userId,
        type: "USER",
        preserveOtherParams: true,
      });
    }
  };

const periodSelectorProps = useMemo(() => ({
  // ✅ אם viewedType === "FAMILY" - שלח contactIds, אחרת שלח userId
  ...(viewedType === "FAMILY"
    ? { contactIds: contacts.map((c) => c.id!).filter(Boolean) }
    : { userId: contacts[0]?.id }),
  value: selectedPeriod,
  type: selectedPeriodType,
  onValueChange: (period: string, type: "REGULAR" | "PRE_ADVICE") => {
    setSelectedPeriod(period);
    setSelectedPeriodType(type);
  },
  onPeriodsLoaded: (periods: ReportedPeriod[]) => {
    if (!id) return;
    const existing = reportedPeriodsRef.current[id];
    const newHash = JSON.stringify(periods);
    const existingHash = JSON.stringify(existing);
    if (newHash === existingHash) {
      // console.log("🔁 התקופות זהות – לא מתבצע עדכון");
      return;
    }
    // console.log("✅ שינוי בתקופות – מתבצע עדכון");
    // console.log("Received periods for user:", id, periods);
    reportedPeriodsRef.current[id] = periods;
    setReportedPeriods(periods);
  }
}), [contacts, selectedPeriod, selectedPeriodType, id, viewedType]);

  // ✅ בדיקה אם צריך להציג קומפוננטת הזמנת טרום ייעוץ
  const shouldShowPreAdviceRequest = useMemo(() => {
    if (viewedType !== "USER" || !contacts[0]) return false;
    const contact = contacts[0];
    const accessType = contact.data_access_type;
    const isAllowed = accessType === undefined || accessType === null || accessType === DataAccessTypeEnum.FULL;
    if (!isAllowed) return false;
    return !contact.last_data_reported_period && !contact.last_pre_advice_data_period;
  }, [viewedType, contacts]);

  // ✅ יצירת המערך של TABS - כולל את כל הטאבים
  const staticTabs  = useMemo(() => [
    // {
    //   label: "מפת כספים",
    //   content: (
    //     // <>
    //     //   {hasPreAdviceData && (
    //     //     <Tabs value={subTab} onChange={handleSubTabChange} centered>
    //     //       <Tab label="נתונים אחרונים" />
    //     //       <Tab label="טרום ייעוץ" />
    //     //     </Tabs>
    //     //   )}

    //     //   {subTab === 0 && <AccountAndFundsContainer mimshaks={filteredMimshaks} userPrivacyDataList={[userPrivacyData]} />}
    //     //   {subTab === 1 &&
    //     //     (filteredPreAdviceMimshaks.length > 0 ? (
    //     //       <AccountAndFundsContainer mimshaks={filteredPreAdviceMimshaks} userPrivacyDataList={[userPrivacyData]} />
    //     //     ) : (
    //     //       <div style={{ textAlign: "center", marginTop: "20px" }}>
    //     //         <h3>לא נמצאו מסמכי טרום יעוץ</h3>
    //     //         <MiniUploadFile onFileUploaded={handleFileUpload} />
    //     //       </div>
    //     //     ))}
    //     // </>
    //     <AccountAndFundsContainer mimshaks={flatMimshaks} userPrivacyDataList={privacyDataList} reporedPeriods={reportedPeriods} fundData={fundData} />
    //   ),
    // },
    {
      label: "מעבדה",
      icon: <Favorite sx={{ color: "pink", fontSize: 20 }} />,
      content: (<>
     {/* <AllMimshaksByProductSavingType mimshaks={subTab === 1 ? filteredPreAdviceMimshaks : filteredMimshaks} />
     <AllMismaksMinDisplay mimshaks={subTab === 1 ? filteredPreAdviceMimshaks : filteredMimshaks} /> */}
    {/* <MimshakScatterCharts mimshakData={mimshakData} /> */}
     <FeesLabPageDnD key={`${id}-${viewedType}`} data={mimshakData}
          id={id}
          familyId={viewedType=="FAMILY" ? id : undefined}
          selectedPeriodType={selectedPeriodType}
            contacts={contacts} selectedPeriod={selectedPeriod} />
       {/* <AllMimshaksByProductSavingType mimshaks={flatMimshaks} /> */}
     {/* <AllMismaksMinDisplay mimshaks={flatMimshaks} /> */}
     </>)
    },
      { label: "ראשי", content: (<>
        {/* <AllMimshaksByProductSavingType mimshaks={subTab === 1 ? filteredPreAdviceMimshaks : filteredMimshaks} />
        <AllMismaksMinDisplay mimshaks={subTab === 1 ? filteredPreAdviceMimshaks : filteredMimshaks} /> */}
         {/* <UserTaskUploadTable users={users} /> */}
          {/* <UserPoliciesComparisonTable userId={contacts?.[0]?.id} products={allProducts} periods={reportedPeriods} />
          <AllPolicyTables
              data={mimshakData}
              viewedType={viewedType}
              selectedPeriodType={selectedPeriodType}
           /> */}
           {shouldShowPreAdviceRequest && (
            // ✅ הצגת קומפוננטת הזמנת טרום ייעוץ אם אין תאריכי דיווח
            <PreAdviceRequestComponent
              contactId={contacts[0]?.id!}
              contactName={contacts[0] ? `${contacts[0].first_name || ''} ${contacts[0].last_name || ''}`.trim() : undefined}
              nextActivityDate={contacts[0]?.next_activity_date}
              leadSource={contacts[0]?.lead_source}
              leadSourceText={contacts[0]?.lead_source_text}
            />
          )}
           <MainPage
              data={mimshakData}
              viewedType={viewedType}
              selectedPeriod={selectedPeriod}
              selectedPeriodType={selectedPeriodType}
              periods={reportedPeriods}
              products={allProducts}
              contacts={contacts}
              pensionMimshakData={pensionMimshakData}
              capitalMimshakData={capitalMimshakData}
              insuranceMimshakData={insuranceMimshakData}
            />

        </>)},
    { label: "הפקדות", content: (
      <Suspense fallback={<CircularProgress />}>
        <DepositsPage
          contacts={contacts}
          mimshakData={mimshakData}
          selectedPeriod={selectedPeriod}
          viewedType={viewedType}
          allProducts={allProducts}
          onEmploymentStatusClick={(contact: Contact) => {
            setContactInfoContacts([contact]);
            setContactInfoSection("employment");
            setContactInfoOpened(true);
            setContactInfoOpen(true);
          }}
        />
      </Suspense>
    ) },

    { label: "פנסיה", content:
       <Suspense fallback={<CircularProgress />}>
         <PensionPage
           pensionMimshakData={pensionMimshakData}
           allMimshakData={mimshakData}
           pensionProducts={pensionProducts}
           contacts={contacts}
           id={id}
           familyId={viewedType === "FAMILY" ? id : undefined}
           selectedPeriod={selectedPeriod}
           selectedPeriodType={selectedPeriodType}
         />
       </Suspense> },
    { label: "ביטוחים ומקרי קיצון", content: (
      <ContactsInsurancePage
        mimshakData={mimshakData}
        insuranceMimshakData={insuranceMimshakData}
        viewedType={viewedType}
        contacts={contacts}
        insuranceProducts={insuranceProducts}
        selectedPeriod={selectedPeriod ?? ""}
        id={id}
        selectedPeriodType={selectedPeriodType}
      />
    ) },
    { label: "גרף צמיחה", content: <Suspense fallback={<CircularProgress />}>
      <GrowthPage
        contacts={contacts}
        id={id}
        viewedType={viewedType}
        mimshakData={mimshakData}
        reportedPeriod={selectedPeriod}
      />
      </Suspense> },
    // ✅ טאבים שהיו קודם ב-dynamicTabs
    {
      label: "סטורי",
      content: (
        <Suspense fallback={<CircularProgress />}>
          <StoryPage
            mimshakData={mimshakData}
            contacts={contacts}
            name={name}
            selectedPeriodType={selectedPeriodType as "REGULAR" | "PRE_ADVICE"}
            flatMimshaks={flatMimshaks}
            reportedPeriods={reportedPeriods}
            fundData={fundData}
            selectedPeriod={selectedPeriod}
            liquidProducts={liquidProducts}
            capitalMimshakData={capitalMimshakData}
            pensionMimshakData={pensionMimshakData}
            pensionProducts={pensionProducts}
            insuranceMimshakData={insuranceMimshakData}
            insuranceProducts={insuranceProducts}
          />
        </Suspense>
      ),
    },
    {
      label: "פיזור",
      content: (
        <>
        <ExposurePage
            mimshakData={Array.from(
              new Map(
                [...capitalMimshakData, ...pensionMimshakData].map(item => [item.id, item])
              ).values()
            )}
            contacts={contacts}
            selectedReportedPeriod={selectedPeriod}
            selectedPeriodType={selectedPeriodType as "REGULAR" | "PRE_ADVICE"}
            viewedType={viewedType}
            familyId={viewedType === "FAMILY" ? id : undefined}
          />
          {/* <ExposureGauges policies={allPolicies} fundData={fundData || []} />
          <PolicyFundYieldsChart policies={allPolicies}  />
          <InvestmentPathTable policies={allPolicies} fundData={fundData || []} /> */}
        </>
      ),
    },
    {
      label: "הבנק שלי",
      content: (
        <SelfBankPage
          contacts={contacts}
          mimshakData={mimshakData}
          selectedPeriod={selectedPeriod}
          viewedType={viewedType}
          name={name}
          fundData={fundData}
        />
      ),
    },
  ], [
    flatMimshaks,
    allPolicies,
    allProducts,
    pensionProducts,
    liquidProducts,
    insuranceProducts,
    insuranceMimshakData,
    pensionMimshakData,
    contacts,
    familyMembers,
    shouldShowPreAdviceRequest,
    mimshakData,
    viewedType,
    selectedPeriod,
    selectedPeriodType,
    reportedPeriods,
    fundData,
    id,
    name,
  ]);
 
  const tabOrder = [
    "ראשי",
    "הפקדות",
    "פנסיה",
    "פיזור",
    "מעבדה",
    "ביטוחים ומקרי קיצון",
    "סטורי",
    "הבנק שלי",
    "גרף צמיחה",
  ];

  const tabs = useMemo(() => {
    // מיון לפי הסדר שהוגדר ב-tabOrder
    return staticTabs.sort(
      (a, b) => tabOrder.indexOf(a.label) - tabOrder.indexOf(b.label)
    );
  }, [staticTabs]);

  return (
    <Box
    sx={{
      height: "100vh", // ✅ גובה כל המסך
      overflow: "hidden", // ✅ מניעת גלילה חיצונית
      position: "relative", // ✅ שמירה על יציבות פריסה
      display: "flex", // ✅ שליטה על פריסה פנימית
      flexDirection: "column", // ✅ ארגון הטאבים והתוכן
      direction: "rtl", // ✅ כיוון טקסט
    }}
  >
     {/* כפתור צף סיכום פגישה */}
     <Box
        sx={{
          position: "absolute",
          top: 10,
          left: 16,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Fab
          color="primary"
          sx={{
            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
          }}
          onClick={() => setMeetingSummaryOpen(true)}
        >
          <SummarizeIcon />
        </Fab>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#424242",
            textAlign: "center",
          }}
        >
          סיכום פגישה
        </Typography>
        <Fab
          sx={{
            backgroundColor: "pink",
            color: "inherit",
            boxShadow: "0 4px 12px rgba(233, 30, 99, 0.3)",
            "&:hover": { backgroundColor: "#f8bbd0" },
          }}
          onClick={() => { setCommentsOpened(true); setCommentsOpen(true); }}
        >
          <NoteAltOutlinedIcon />
        </Fab>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#424242",
            textAlign: "center",
          }}
        >
          הערות
        </Typography>
      </Box>

      {/* Drawer פרטי לקוח - נפתח בלחיצה על קוביית שכר */}
      {contactInfoOpened && (
        <Suspense fallback={<CircularProgress />}>
          <ConsultingContactInfo
            open={contactInfoOpen}
            onClose={() => setContactInfoOpen(false)}
            allMimshakData={mimshakData}
            contacts={contactInfoContacts}
            initialSection={contactInfoSection}
            viewedType={viewedType}
          />
        </Suspense>
      )}

      {/* Drawer הערות - טעינה עצלה */}
      {commentsOpened && (
        <StyledDrawer
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          onBack={() => setCommentsOpen(false)}
          width={560}
          anchor="left"
        >
          <Suspense fallback={<CircularProgress />}>
            {commentsOpen && (
              <ContactCommentsPanel
                contacts={contacts}
                isFamily={viewedType === "FAMILY"}
              />
            )}
          </Suspense>
        </StyledDrawer>
      )}

      {/* Drawer סיכום פגישה - טעינה עצלה */}
      {meetingSummaryOpen && (
        <Suspense fallback={<CircularProgress />}>
          <MeetingSummaryDrawer
            open={meetingSummaryOpen}
            onClose={() => setMeetingSummaryOpen(false)}
            selectedPeriod={selectedPeriod}
            selectedPeriodType={selectedPeriodType}
            allMimshakData={mimshakData}
            contacts={contacts}
          />
        </Suspense>
      )}

     {!isFullLoading ? (
        <>
          {/* ✅ תמיד להציג את ה-Header */}
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <ConsultingHeader
                id={id}
                name={name}
                contacts={contacts}
                mimshaks={flatMimshaks}
                allMimshakData={mimshakData}
                viewedType={viewedType}
                handleFamilyMemberClick={handleFamilyMemberClick}
                familyInfo={familyInfo ?? undefined}
                selectedPeriodType={selectedPeriodType}
              />
            </Grid>
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <PeriodDropdownSelector {...periodSelectorProps} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, minWidth: 0, pr: 1 }}>
                  {viewedType === "USER" && familyInfo && (
                    <Chip
                      avatar={
                        <Avatar sx={{ bgcolor: "#4caf50 !important" }}>
                          <GroupsIcon sx={{ color: "white" }} />
                        </Avatar>
                      }
                      label={`משפחת ${familyInfo.familyName}`}
                      onClick={() => handleFamilyClick(familyInfo.familyId)}
                      variant="outlined"
                      clickable
                      sx={{ fontWeight: 500, flexShrink: 0 }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <BeneficiaryChips products={allProducts} handleFamilyMemberClick={handleFamilyMemberClick} />
                  </Box>
                </Box>
              </Grid>
            </>
          </Grid>

          <Box
            sx={{
              height: "100vh", // ✅ ממלא את כל גובה המסך
              overflow: "hidden", // ✅ מונע גלילה חיצונית
              position: "relative", // ✅ מונע גלילה בלתי רצויה
            }}
          >
            <ResponsiveConsultingTabs tabs={tabs} />
          </Box>
        </>
      ) : (
        <p>טוען נתונים...</p>
      )}
    </Box>
  );
};
